<template>
	<div id="glossary-results" aria-live="polite" role="region">
		<div :id="`term-${term.GLOSSARY_ENTRY_ID}`"
				 class="term" v-for="(term, termIndex) in terms"
				 :key="term.GLOSSARY_ENTRY_ID">
			<div class="row" v-if="hasPermission( term.STUDENT_RID ) && editing ===  term.GLOSSARY_ENTRY_ID">
				<div class="form-group col-xs-12 col-md-6">
					<label :for="`name-${term.GLOSSARY_ENTRY_ID}`" class="sr-onl"></label>
					<input :id="`name-${term.GLOSSARY_ENTRY_ID}`" type="text" v-model="term.TERM" class="form-control" />
				</div>
			</div>
			<!-- Term Display Start -->
			<h2 :id="term.TERM" class="term-title" v-else style="padding-top: 20px;">
				<span :class="{ 'text-danger' : term.APPROVED_FLG === 'N' }">{{ term.TERM }}</span>
				<button class="btn-unstyled"
								data-toggle="popover"
								data-html="true"
								v-tooltip.top-center="{content: $t('MAIN_CONTENT.GlossaryResults.awaitingTeacherApproval'), trigger: 'hover click'}"
								v-if="term.APPROVED_FLG === 'N'">
					<i class="fa fa-exclamation-circle text-danger" aria-hidden="true"></i>
				</button>
				<button class="btn-unstyled text-muted"
								role="button"
								:aria-label="`Edit-${term.GLOSSARY_ENTRY_ID}`"
								v-if="hasPermission( term.STUDENT_RID )"
								@click="onEdit(term.GLOSSARY_ENTRY_ID, 'name')">
					<i class="fas fa-pencil-alt" aria-hidden="true"></i>
				</button>
			</h2>
			<!-- Term Display End -->

			<!-- Author Name and Date Start -->
			<div class="author text-sm text-muted">
				{{ $t('MAIN_CONTENT.GlossaryResults.authorWithColon') }} 
				<strong class="name">
					{{ term.DISPLAY_NAME }}
				</strong> 
				<span>
					{{ term.CREATED_DATE }}
				</span>
			</div>
			<!-- Author Name and Date End -->

			<div class="form-group" v-if="hasPermission(term.STUDENT_RID) && editing === term.GLOSSARY_ENTRY_ID">
				<ckeditor4 v-model="term.DEFINITION"
									:editor="editor"
									:config="editorConfig"
									@ready="onEditorReady">
				</ckeditor4>
			</div>
			<div :id="`def-${term.GLOSSARY_ENTRY_ID}`"
						class="definition"
						:class="{'editable': hasPermission( term.STUDENT_RID ) }"
						v-html="term.DEFINITION"
						v-else
						@click="onEdit( term.GLOSSARY_ENTRY_ID, 'definition' )">
			</div>
			<div class="class-items"
					 v-if="term.FILES && term.FILES.length">
				<div class="flex-container box">
					<!-- icon -->
					<div class="category-icon" :class="getColorClass('O')">
						<div class="icon" @click="isFolderOpen = !isFolderOpen" style="cursor: pointer;">
							<font-awesome-icon icon="folder-open" v-if="isFolderOpen"></font-awesome-icon>
							<font-awesome-icon icon="folder" v-else></font-awesome-icon>
						</div>
					</div>

					<div class="content-details" style="background-color: #FFFFFF; border: 1px solid #EDEDED;">
						<a href="javascript:void(0)" @click="toggleFolder()">
							<h3>{{ $t('MAIN_CONTENT.GlossaryResults.attachedFilesTitleCase') }}</h3>
						</a>
						<p></p>
						<div class="folder-items" v-if="isFolderOpen">
							<div v-for="(file, fileIndex) in term.FILES" :key="`file_${fileIndex}`">
								<font-awesome-icon icon="file-word"
																	 v-if="file.CONTENT_TYPE === 'msword'">
								</font-awesome-icon>
								<font-awesome-icon :icon="['far', 'file-pdf']"
																	 v-else-if="file.CONTENT_TYPE === 'pdf'">
								</font-awesome-icon>
								<font-awesome-icon :icon="['far', 'file-excel']"
																	 v-else-if="file.CONTENT_TYPE === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'">
								</font-awesome-icon>
								<font-awesome-icon :icon="['far', 'file-powerpoint']"
																	 v-else-if="file.CONTENT_TYPE === 'vnd.openxmlformats-officedocument.presentationml.presentation'">
								</font-awesome-icon>
								<font-awesome-icon icon="file" v-else>
								</font-awesome-icon> 
								<a :href="file.ATTACHMENT_URL" target="_blank">
									{{ file.CONTENT_FILENAME }}
								</a>
								<font-awesome-icon class="pull-right"
																	 icon="trash"
																	 @click="term.FILES.splice(fileIndex, 1)"
																	 v-if="editing === term.GLOSSARY_ENTRY_ID">
								</font-awesome-icon>
							</div>
						</div>
					</div>
				</div>
			</div>
			<button class="btn btn-default"
							role="button"
							:aria-label="`Edit ${term.TERM}`"
							@click="onEdit(term.GLOSSARY_ENTRY_ID, 'definition')"
							v-if="hasPermission(term.STUDENT_RID) && editing !== term.GLOSSARY_ENTRY_ID">
				<font-awesome-icon icon="pencil-alt"></font-awesome-icon> {{ $t('MAIN_CONTENT.GlossaryResults.edit') }}
			</button>
			<button class="btn btn-default"
							role="button"
							:aria-label="`Delete ${term}`"
							@click="deleteTerm(term)"
							v-if="hasPermission(term.STUDENT_RID) && editing !== term.GLOSSARY_ENTRY_ID">
				<font-awesome-icon icon="trash"></font-awesome-icon> {{ $t('MAIN_CONTENT.GlossaryResults.delete') }}
			</button>
			<div v-if="hasPermission(term.STUDENT_RID) && editing === term.GLOSSARY_ENTRY_ID">
				<file-uploader
					ref="fileUpload"
					:nameHeader="$t( 'MAIN_CONTENT.commonContent.name' )"
					:sizeHeader="$t( 'MAIN_CONTENT.commonContent.size' )"
					:removeHeader="$t( 'MAIN_CONTENT.commonContent.remove' )"
					:dropSingleFile="$t( 'MAIN_CONTENT.commonContent.dropSingleFile' )"
					:dropMultipleFiles="$t( 'MAIN_CONTENT.commonContent.dropMultipleFiles' )"
					:selectSingleFile="$t( 'MAIN_CONTENT.commonContent.selectSingleFile' )"
					:selectMultipleFiles="$t( 'MAIN_CONTENT.commonContent.selectMultipleFiles' )"
					:attachFile="$t( 'MAIN_CONTENT.commonContent.attachFile' )"
					:attachFiles="$t( 'MAIN_CONTENT.commonContent.attachFiles' )"
					:files="term.NEW_FILES"
					:termData="term"
					:postAction="`/PortalAPI/index.cfm/api/v1/class-pages/${$route.params.courseSectionGUID}/files?student_rid=${selectedStudent.STUDENT_RID}`"
					:auth="{'Authorization': `bearer ${accessToken}`, 'Client-ID': $store.state.clientID}"
					:URL="externalFileData"
					@saveGoogleFilesData="saveGoogleFileData"
					@saveDropboxFilesData="saveDropboxFileData"
					@saveOneDriveFilesData="saveOneDriveFileData"
					@updateFiles="updateValue(term, termIndex)"
				>
				</file-uploader>
			</div>
			<div class="teacher-note-wrap" v-if="term.ENTRY_NOTES !== '' && editing !== term.GLOSSARY_ENTRY_ID">
				<p><strong>{{ $t( 'MAIN_CONTENT.GlossaryResults.teacherNote' ) }}</strong></p>
				<span v-html="term.ENTRY_NOTES"></span>
			</div>
			<div v-if="hasPermission( term.STUDENT_RID ) && editing === term.GLOSSARY_ENTRY_ID" class="form-group text-right">
				<button class="btn btn-default" @click="onCancel">{{ $t('commonText.cancel') }}</button>
				<button class="btn btn-success" @click="save(term)">{{ $t('commonText.save') }}</button>
			</div>
		</div>
	</div>
</template>

<script>
	import { mapGetters, mapActions } from "vuex";
	import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
	import { VTooltip, VPopover, VClosePopover } from 'v-tooltip';

	// Shared components in vuecommons
	import FileUploader from 'COMMONS/shared/FileUploader.vue';

	// API
	import GlossaryEntries from '../../../api/GlossaryEntries';
	import ExternalFilePicker from '../../../api/ExternalFilePicker';
	import ExternalFilesPost from '../../../api/ExternalFilesPost';

	import { buildFileData } from './buildFileData';

	import ckeditor4 from '../../ckeditor4.vue';
	import editorConfigObj from '../../shared/editorConfigObj';
	/**
		* @vue-prop {Array} [entries=[]] - Array of the glossary entries.
		* @vue-prop {String} [type='glossaries'] - String to state what type of glossary to access
		* @vue-data {Boolean} [isFolderOpen=true] - Array of the glossary entries.
		* @vue-data {String} [editing=''] - Entry ID that is in edit mode.
		* @vue-data {String} [areaInFocus=''] - Name of area in focus.
		* @vue-data {Object} [editor=ClassicEditor] - Editor .
		* @vue-data {Object} editorConfig - Configuration of the editor.
		* @vue-data {Array} files - List of files in glossary entry
		* @vue-computed {Array} terms - The glossary terms.
	*/
	export default {
		props: {
			entries: {
				type: Array,
				default: function() {
					return [];
				}
			},
			type: {
				type: String,
				default: 'glossaries'
			},
			canEdit: {
				type: Boolean,
				required: true
			}
		},
		data() {
			return {
				isFolderOpen: true,
				editing: '',
				areaInFocus: '',
				editor: null,
				editorConfig: editorConfigObj,
				externalFileData: {
					API_ORIGIN_URL:							'',
					DISTRICT_ID:								'',
					DROPBOX_API_CLIENT_ID:			'',
					DROPBOX_API_CLIENT_SECRET:	'',
					GOOGLE_API_APP_ID:					'',
					GOOGLE_API_CLIENT_ID:				'',
					GOOGLE_API_CLIENT_SECRET:		'',
					ONEDRIVE_API_CLIENT_ID:			'',
					ONEDRIVE_API_CLIENT_SECRET:	''
				},
				files: []
			};
		},
		computed: {
			...mapGetters(['role', 'accessToken', 'selectedStudent']),
			terms() {
				return this.entries;
			}
		},
		components: {
			ckeditor4,
			FontAwesomeIcon,
			FileUploader
		},
		directives: {
			'tooltip': VTooltip,
		},
		methods: {
			...mapActions(['fetchToken']),
			/**
				* Checks if the entry's author is the logged-in user.
				* @param {number} studentRID - Students RID number
			*/
			hasPermission(studentRID) {
				return this.role === 'student' && +studentRID === +this.selectedStudent.STUDENT_RID && this.canEdit;
			},
			/**
				* Clears editing flag
			*/
			onCancel() {
				this.editing = "";
			},
			/**
				* Sets focus on the area to be edited.
				* @param {String} id - The entry id
				* @param {String} area - The area name in focus
			*/
			onEdit(id, area) {
				// glossary entry id
				this.editing = id;
				this.areaInFocus = area;

				this.fetchToken();
			},
			/**
				* Sets focus on the editor if the focus area is "definition".
				* @param {Object} editor - The editor instance
			*/
			onEditorReady(editor) {
				if(this.areaInFocus === "definition") {
					editor.editing.view.focus();
				}
			},
			/**
				* Gets the color class, based on the type of report.
				* @param {String} code - The report type code
			*/
			getColorClass(code) {
				return `container-${this.$options.filters.mappedColor("REPORT-TYPE-" + code)}`;
			},
			/**
				* Toggles between open and close for a folder.
				* @param {String} id - The item /folder id
			*/
			toggleFolder() {
				this.isFolderOpen = !this.isFolderOpen;
			},
			updateValue(term, termIndex) {
				term.NEW_FILES = this.$refs.fileUpload[termIndex].$refs.upload.files;
			},
			getExternalData() {
				const self = this;

				const external = new ExternalFilePicker({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID
					}
				});

				external.get().then(response => {
					self.$set(self, 'externalFileData', external.getResponseData());
				});
			},
			save(term) {
				const self = this;

				for(let n in term.NEW_FILES) {
					term.FILES.push(term.NEW_FILES[n].response.data);
				}

				const glossary = new GlossaryEntries({
					routeParams: {
						courseSectionGuid: self.$route.params.courseSectionGUID,
						glossaryID: self.$route.params.glossaryID,
						glossaryType: self.type,
						glossaryEntryID: term.GLOSSARY_ENTRY_ID
					},
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
					},
					data: JSON.stringify(term)
				});

				glossary.put().then(response => {
					if(!glossary.getResponseData().error) {
						self.$emit('clearResults');
						self.editing = '';
					}
				});
			},
			deleteTerm(term) {
				const self = this;

				const glossary = new GlossaryEntries({
					routeParams: {
						courseSectionGuid: self.$route.params.courseSectionGUID,
						glossaryID: self.$route.params.glossaryID,
						glossaryType: self.type,
						glossaryEntryID: term.GLOSSARY_ENTRY_ID
					},
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
					}
				});

				glossary.delete().then(response => {
					if(!glossary.getResponseData().error) {
						self.$emit('clearTerm', term);
					}
				});
			},
			saveGoogleFileData(file, term) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'googleDrive',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						file_id: file.id,
						authorization: 'Bearer ' + file.authToken,
						file_name: file.name,
						file_mime: file.mimeType,
						webContentLink: file.webContentLink,
						webViewLink: file.webViewLink
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					let dataObj = buildFileData(
						self.$route.params.courseSectionGUID,
						self.selectedStudent.STUDENT_RID,
						self.accessToken,
						self.$store.state.CLIENT_ID,
						data
					);

					let hasFile = false;

					for(let f in term.NEW_FILES) {
						let file = term.NEW_FILES[f];

						if(file.response.data.HASHA === data.HASHA && file.response.data.HASHB === data.HASHB) {
							hasFile = true;
							break;
						}
					}

					if(!hasFile && dataObj.response.data.HASHA && dataObj.response.data.HASHB) {
						term.NEW_FILES.push(dataObj);
					}
				});
			},
			saveDropboxFileData(file, term) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'dropbox',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						authorization: file.authToken,
						file_name: file.name,
						web_content_link: file.link
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					let dataObj = buildFileData(
						self.$route.params.courseSectionGUID,
						self.selectedStudent.STUDENT_RID,
						self.accessToken,
						self.$store.state.CLIENT_ID,
						data
					);

					let hasFile = false;

					for(let f in term.NEW_FILES) {
						let file = term.NEW_FILES[f];

						if(file.response.data.HASHA === data.HASHA && file.response.data.HASHB === data.HASHB) {
							hasFile = true;
							break;
						}
					}

					if(!hasFile && dataObj.response.data.HASHA && dataObj.response.data.HASHB) {
						term.NEW_FILES.push(dataObj);
					}
				});
			},
			saveOneDriveFileData(file, term) {
				const self = this;

				const external = new ExternalFilesPost({
					query: {
						student_rid: self.selectedStudent.STUDENT_RID,
						source: 'oneDrive',
						fielId: file.id
					},
					routeParams: {
						course_section_guid: self.$route.params.courseSectionGUID
					},
					data: JSON.stringify({
						file_name: file.name,
						web_content_link: file['@content.downloadUrl']
					})
				});

				external.post().then(response => {
					let data = external.getResponseData();
					let dataObj = buildFileData(
						self.$route.params.courseSectionGUID,
						self.selectedStudent.STUDENT_RID,
						self.accessToken,
						self.$store.state.CLIENT_ID,
						data
					);

					let hasFile = false;

					for(let f in term.NEW_FILES) {
						let file = term.NEW_FILES[f];

						if(file.response.data.HASHA === data.HASHA && file.response.data.HASHB === data.HASHB) {
							hasFile = true;
							break;
						}
					}

					if(!hasFile && dataObj.response.data.HASHA && dataObj.response.data.HASHB) {
						term.NEW_FILES.push(dataObj);
					}
				});
			}
		},
		created() {
			this.getExternalData();
		}
	};
</script>
